/**
 * Collection management utility for ceSDK editor uploads.
 * Handles finding or creating the "Editor Uploads" collection.
 * Identifies collection by integrationType, not by name.
 */

import { createCollection } from "../api/media-library/create-collection";
import { getCollections } from "../api/media-library/get-collections";
import { MediaCollection } from "../api/media-library/types";

const EDITOR_UPLOADS_COLLECTION_NAME = "Editor Uploads";
const EDITOR_UPLOADS_INTEGRATION_TYPE = "cesdk-editor-uploads";
let cachedCollectionId: string | null = null;
let pendingPromise: Promise<string> | null = null;

/**
 * Get or create the "Editor Uploads" collection.
 * Identifies collection by integrationType, not by name.
 * Caches the collection ID to avoid repeated API calls.
 * Uses a pending promise to prevent race conditions.
 * @returns The collection ID (_id field)
 */
export async function getOrCreateEditorUploadsCollection(): Promise<string> {
  if (cachedCollectionId) {
    return cachedCollectionId;
  }

  if (pendingPromise) {
    return pendingPromise;
  }

  pendingPromise = (async () => {
    try {
      const collections = await getCollections();

      const existingCollection = collections.find(
        (collection) =>
          collection.integrationType === EDITOR_UPLOADS_INTEGRATION_TYPE
      );

      if (existingCollection) {
        cachedCollectionId = existingCollection._id;
        return cachedCollectionId;
      }

      const collection = await createCollection({
        name: EDITOR_UPLOADS_COLLECTION_NAME,
        integrationType: EDITOR_UPLOADS_INTEGRATION_TYPE,
      });

      if (!collection._id) {
        throw new Error(
          `Failed to create Editor Uploads collection: missing _id in response. Received: ${JSON.stringify(collection)}`
        );
      }

      cachedCollectionId = collection._id;
      return cachedCollectionId;
    } catch (error) {
      console.error(
        "Failed to get or create Editor Uploads collection:",
        error
      );
      throw error;
    } finally {
      pendingPromise = null;
    }
  })();

  return pendingPromise;
}

/**
 * Get the cached Editor Uploads collection ID.
 * Returns null if collection hasn't been initialized yet.
 * @returns The collection ID or null
 */
export async function getEditorUploadsCollectionId(): Promise<string | null> {
  if (cachedCollectionId) {
    return cachedCollectionId;
  }

  try {
    // Try to find existing collection by integrationType
    const collections = await getCollections();
    const existingCollection = collections.find(
      (collection) =>
        collection.integrationType === EDITOR_UPLOADS_INTEGRATION_TYPE
    );

    if (existingCollection) {
      cachedCollectionId = existingCollection._id;
      return cachedCollectionId;
    }

    return null;
  } catch (error) {
    console.error("Failed to get Editor Uploads collection ID:", error);
    return null;
  }
}

/**
 * Check if a collection is the Editor Uploads collection.
 * @param collection The collection to check
 * @returns true if this is the Editor Uploads collection
 */
export function isEditorUploadsCollection(
  collection: Pick<MediaCollection, "integrationType">
): boolean {
  return collection.integrationType === EDITOR_UPLOADS_INTEGRATION_TYPE;
}

/**
 * Clear the cached collection ID and pending promise.
 * Useful for testing or when the collection might have been deleted.
 */
export function clearEditorUploadsCollectionCache(): void {
  cachedCollectionId = null;
  pendingPromise = null;
}

/**
 * Delete a file from the Editor Uploads collection
 * @param fileId - The asset ID to delete
 */
export async function deleteEditorUploadsFile(fileId: string): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { default: MediaLibraryLegacyApi } =
      await import("../api/media-library/MediaLibraryLegacyApi");
    const api = new MediaLibraryLegacyApi();
    await api.deleteEntry(fileId);
  } catch (error) {
    console.error("Failed to delete file from Editor Uploads:", error);
    throw new Error(
      `Failed to delete file: ${error instanceof Error ? error.message : "Unknown error"}`,
      { cause: error }
    );
  }
}
